/**
 * Funciones para obtener períodos anteriores basados en fecha actual
 */

/**
 * Obtiene el mes actual a la fecha proporcionada
 * @param {Date} date - Fecha base (opcional, por defecto fecha actual)
 * @returns {Object} Objeto con informacion del mes anterior
 */
export function obtenerMesActual(date = new Date()) {
  const dateBase = new Date(date);
  const month = dateBase.getMonth();
  //primer día del mes
  const firstDay = new Date(dateBase.getFullYear(), dateBase.getMonth(), 1);
  return {
    initialDate: formatearFecha(firstDay),
    finalDate: formatearFecha(dateBase),
    year: dateBase.getFullYear(),
    month: month + 1,
    range: `${dateBase.toLocaleDateString("es-ES", { month: "long", year: "numeric" })}`,
  };
}

/**
 * Obtiene el mes anterior a la fecha proporcionada
 * @param {Date} date - Fecha base (opcional, por defecto fecha actual)
 * @returns {Object} Objeto con informacion del mes anterior
 */
export function obtenerMesAnterior(date = new Date()) {
  const dateBase = new Date(date);
  // Crear fecha del mes anterior
  const oldMonth = new Date(dateBase.getFullYear(), dateBase.getMonth() - 1, 1);
  //ultimo día del mes anterior
  const endDayPrevMonth = new Date(dateBase.getFullYear(), dateBase.getMonth(), 0);
  return {
    initialDate: formatearFecha(oldMonth),
    finalDate: formatearFecha(endDayPrevMonth),
    year: oldMonth.getFullYear(),
    month: oldMonth.getMonth() + 1,
    range: `${oldMonth.toLocaleDateString("es-ES", { month: "long", year: "numeric" })}`,
  };
}

/**
 * Obtiene el año actual a la fecha proporcionada
 * @param {Date} date - Fecha base (opcional, por defecto fecha actual)
 * @returns {Object} Objeto con informacion del año anterior
 */
export function obtenerAñoActual(date = new Date()) {
  const dateBase = new Date(date);
  const year = dateBase.getFullYear();
  const initialDate = new Date(year, 0, 1);
  return {
    initialDate: formatearFecha(initialDate),
    finalDate: formatearFecha(dateBase),
    año: year,
    range: `Año ${year}`,
  };
}

/**
 * Obtiene el año anterior a la fecha proporcionada
 * @param {Date} date - Fecha base (opcional, por defecto fecha actual)
 * @returns {Object} Objeto con informacion del año anterior
 */
export function obtenerAñoAnterior(date = new Date()) {
  const dateBase = new Date(date);
  const lastYear = dateBase.getFullYear() - 1;
  const initialDate = new Date(lastYear, 0, 1);
  const finalDate = new Date(lastYear, 11, 31);
  return {
    initialDate: formatearFecha(initialDate),
    finalDate: formatearFecha(finalDate),
    año: lastYear,
    range: `Año ${lastYear}`,
  };
}

/**
 * Obtiene el trimestre actual de la fecha proporcionada
 * @param {Date} date - Fecha base (opcional, por defecto fecha actual)
 * @returns {Object} Objeto con información del trimestre actual
 */
export function obtenerTrimestreActual(date = new Date()) {
  const dateBase = new Date(date);
  const actualYear = dateBase.getFullYear();
  const actualMonth = dateBase.getMonth();

  // Determinar trimestre actual (0-3)
  const actualQuarter = Math.floor(actualMonth / 3);

  // Calcular mes inicial del trimestre actual (0-11)
  const initialMonth = actualQuarter * 3;
  const lastMonth = initialMonth + 2;

  // Calcular fechas inicial y final del trimestre
  const initialDate = new Date(actualYear, initialMonth, 1);
  const finalDate = new Date(actualYear, lastMonth + 1, 0);

  return {
    initialDate: formatearFecha(initialDate),
    finalDate: formatearFecha(finalDate),
    año: actualYear,
    trimestre: actualQuarter + 1,
    range: `Trimestre ${actualQuarter + 1} ${actualYear}`,
    months: [
      initialDate.toLocaleDateString("es-ES", { month: "long" }),
      new Date(actualYear, initialMonth + 1, 1).toLocaleDateString("es-ES", { month: "long" }),
      new Date(actualYear, initialMonth + 2, 1).toLocaleDateString("es-ES", { month: "long" }),
    ],
  };
}

/**
 * Obtiene el trimestre anterior a la fecha proporcionada
 * @param {Date} date - Fecha base (opcional, por defecto fecha actual)
 * @returns {Object} Objeto con información del trimestre anterior
 */
export function obtenerTrimestreAnterior(date = new Date()) {
  const dateBase = new Date(date);
  const actualYear = dateBase.getFullYear();
  const actualMonth = dateBase.getMonth();
  // Determinar trimestre actual (0-3)
  const actualQuarter = Math.floor(actualMonth / 3);
  let yearLastQuearter = 0;
  let lastQuearter = 0;
  if (actualQuarter === 0) {
    // Si estamos en Q1, el trimestre anterior es Q4 del año pasado
    yearLastQuearter = actualYear - 1;
    lastQuearter = 3;
  } else {
    yearLastQuearter = actualYear;
    lastQuearter = actualQuarter - 1;
  }
  // Calcular mes inicial del trimestre anterior (0-11)
  const initialMonth = lastQuearter * 3;
  const lastMonth = initialMonth + 2;
  const initialDate = new Date(yearLastQuearter, initialMonth, 1);
  const finalDate = new Date(yearLastQuearter, lastMonth + 1, 0);
  return {
    initialDate: formatearFecha(initialDate),
    finalDate: formatearFecha(finalDate),
    año: yearLastQuearter,
    trimestre: lastQuearter + 1,
    range: `Trimestre ${lastQuearter + 1} ${yearLastQuearter}`,
    months: [
      initialDate.toLocaleDateString("es-ES", { month: "long" }),
      new Date(yearLastQuearter, initialMonth + 1, 1).toLocaleDateString("es-ES", { month: "long" }),
      new Date(yearLastQuearter, initialMonth + 2, 1).toLocaleDateString("es-ES", { month: "long" }),
    ],
  };
}

/**
 * Función auxiliar para formatear fechas
 * @param {Date} date - Fecha a formatear
 * @returns {string} Fecha formateada como YYYY-MM-DD
 */
export function formatearFecha(date) {
  return date.toISOString().split("T")[0];
}

/**
 * Función para dar formato al range inicial personalizado
 * @param {Object} option - Contiene la fecha inicial y final
 * @returns {Object} Objeto con información del rango personalizado
 */
export function getRangePersonalized(option) {
  const initialDate = formatearFecha(option.startDate);
  const finalDate = formatearFecha(option.endDate);
  return {
    initialDate,
    finalDate,
    range: `${initialDate} a ${finalDate}`,
  };
}
